import React, { useState, useEffect, useRef } from "react";
import Icon from "../icon";
import {
  calculateScrollThumbHeight,
  onOptionsScroll,
} from '../../helper/select-utils';

let scrollThumbActive = false;

export default function Select({
  className = "input",
  name,
  value,
  textValue,
  disabled,
  onChange,
  error = false,
  children,
  placeholder,
  custom,
  attributes = {},
  testRefs = {},
  activeOption,
}) {
  if (custom) {
    const [showOptions, setShowOptions] = useState(false);
    const [icon, setIcon] = useState(null);
    const [lineThrough, setLineThrough] = useState(false);
    const [scrollThumbHeight, setScrollThumbHeight] = useState(0);

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const optionsOuterRef = useRef(null);
    const optionsInnerRef = useRef(null);
    const scrollbarRef = useRef(null);
    const scrollbarThumbRef = useRef(null);

    useEffect(() => {
      if (showOptions) {
        calculateScrollThumbHeight(
          optionsInnerRef.current,
          scrollbarRef.current,
          scrollbarThumbRef.current,
          (childrenHeight, optionsInnerHeight, scrollbarHeight) => {
            setScrollThumbHeight(
              (optionsInnerHeight / childrenHeight) * scrollbarHeight
            );
          }
        );
        if (activeOption) {
          scrollToActiveOption();
        }
      }
    }, [showOptions]);

    useEffect(() => {
      if (activeOption) {
        setIcon(
            children?.find((child) => child.props.value === activeOption?.id)?.props
                .icon
        );
        setLineThrough(
            children?.find((child) => child.props.value === activeOption?.id)?.props
                .disabled
        );
      }
    }, [activeOption]);

    const onClick = () => {
      if (!disabled) {
        inputRef.current?.blur();
        setShowOptions(!showOptions);
      }
    };
    const onChildrenClick = (icon, disabled) => {
      setLineThrough(disabled);
      setIcon(icon);
      setTimeout(() => setShowOptions(false), 100);
    };
    const onScroll = () =>
      onOptionsScroll(
        optionsInnerRef?.current,
        scrollbarRef?.current,
        scrollbarThumbRef?.current
      );
    const onScrollBarClick = (event) => {
      const y = event.clientY;
      const scrollReference = scrollbarRef.current?.getBoundingClientRect()?.y;
      const scrollTo =
        ((y - scrollReference) / scrollbarRef.current?.offsetHeight) *
        optionsInnerRef.current?.scrollHeight;
      if (scrollTo) {
        optionsInnerRef.current.scrollTo(0, scrollTo - 20);
      }
    };
    const onScrollThumbMouseDown = () => (scrollThumbActive = true);
    const onScrollThumbMouseUp = () => {
      if (scrollThumbActive) {
        scrollThumbActive = false;
      }
    };
    const onMouseMove = (event) => {
      if (scrollThumbActive) {
        const y = event.clientY;
        const scrollReference =
          scrollbarRef.current?.getBoundingClientRect()?.y;
        const scrollTo = y - scrollReference;

        if (scrollTo) {
          optionsInnerRef.current.scrollTo(0, scrollTo);
        }
      }
    };
    const scrollToActiveOption = () => {
      const activeOptionIndex = Array.from(
        optionsInnerRef.current.children
      )?.findIndex((child) => child.classList.contains("is-active"));
      const previousOptionNode =
        optionsInnerRef.current.children[activeOptionIndex - 1];
      let scrollTop = 0;
      if (activeOptionIndex !== 0 && previousOptionNode) {
        scrollTop = previousOptionNode?.offsetTop - 10;
      }
      optionsInnerRef.current.scrollTo(0, scrollTop);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onScrollThumbMouseUp);

    // pass inputRef to children option elements
    const childrenWithProps = children.map((child) => {
      if (React.isValidElement(child)) {
        const props = {
          inputRef,
          onClick: (icon, disabled) => {
            child.props.onClick?.();
            onChildrenClick(icon, disabled);
          },
          active: child.props.value === activeOption?.id,
        };
        return React.cloneElement(child, props);
      }
      return child;
    });

    // register click outside of component to close it
    const registerOnOutsideClickListener = (ref) => {
      useEffect(() => {
        const handleClickOutside = (event) => {
          if (!ref.current?.contains(event.target)) {
            setShowOptions(false);
          }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }, [ref]);
    };

    registerOnOutsideClickListener(wrapperRef);

    return (
      <div className="input-group__custom-select-wrapper" ref={wrapperRef}>
        <div className="input-group__field">
          <input
            type="text"
            className={
              className +
              (error ? " is-error" : "") +
              (showOptions ? " is-active" : "") +
              (icon ? " has-icon" : "") +
              (lineThrough ? " is-line-through" : "")
            }
            placeholder={placeholder}
            value={textValue}
            readOnly
            onClick={onClick}
            ref={inputRef}
          />
          <div className="input__icon--select">
            <Icon name="arrow-down" />
          </div>
          {icon && <Icon name={icon} className="input__icon--value" />}
        </div>
        {showOptions && (
          <>
            <div className="input-group__select-seperator" />
            <div
              className="input-group__select-options-outer"
              ref={optionsOuterRef}
              onMouseUp={onScrollThumbMouseUp}
            >
              <div
                className="input-group__select-options-inner"
                ref={optionsInnerRef}
                onScroll={onScroll}
                onMouseMove={onMouseMove}
              >
                {childrenWithProps}
                {/*custom scrollbar*/}
                {childrenWithProps?.length > 3 && (
                  <div
                    className="input-group__select-options-scrollbar-outer"
                    ref={scrollbarRef}
                    onClick={onScrollBarClick}
                  >
                    <div
                      className="input-group__select-options-scrollbar-inner"
                      ref={scrollbarThumbRef}
                      onMouseDown={onScrollThumbMouseDown}
                      onMouseUp={onScrollThumbMouseUp}
                      style={{ height: scrollThumbHeight }}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  const placeholderOption = (
    <option value="" disabled hidden>
      {placeholder}
    </option>
  );

  return (
    <div className="input-group__field">
      <select
        className={className + (error ? " is-error" : "")}
        name={name}
        value={value}
        disabled={disabled}
        onChange={onChange}
        {...attributes}
        {...testRefs}
      >
        {placeholder ? placeholderOption : null}
        {children}
      </select>
      <div className={"input__icon--select" + (disabled ? " is-disabled" : "")}>
        <Icon name="arrow-down" />
      </div>
    </div>
  );
}
